import React, { FC, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Space,Radio, Button  } from 'antd'
import type { RadioChangeEvent } from 'antd'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import * as go from 'gojs'
import { ReactDiagram } from 'gojs-react'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import type { IAppState } from '@/store/reducers'
import '@/pages/table2Mana/components/gojs.css'
import { getAllProjectInfo,getAllTechnicalRoad } from '@/services/table.service'
import { IProjectInfoResData } from '@/store/models/tableEdit.model'
import Nav from '@/components/nav'

class PoolLayout extends go.GridLayout {
    public MINLENGTH=200
    public MINBREADTH=100
    constructor() {
        super()
        this.cellSize = new go.Size(1, 1)
        this.wrappingColumn = Infinity
        this.wrappingWidth = Infinity
        this.spacing = new go.Size(0, 0)
        this.alignment = go.GridLayout.Position
    }

    doLayout(coll:any) {
        const diagram = this.diagram
        if (diagram === null) return
        diagram.startTransaction('PoolLayout')
        // make sure all of the Group Shapes are big enough
        // const minlen = this.MINLENGTH
        diagram.findTopLevelGroups().each(lane => {
            if (!(lane instanceof go.Group)) return
            const shape = lane.selectionObject
            if (shape !== null) {  // change the desiredSize to be big enough in both directions
                const sz = this.computeLaneSize(lane)
                shape.width = (!isNaN(shape.width)) ? Math.max(shape.width, sz.width) : sz.width
                // if you want the height of all of the lanes to shrink as the maximum needed height decreases:
                // shape.height = minlen
                // if you want the height of all of the lanes to remain at the maximum height ever needed:
                //shape.height = (isNaN(shape.height) ? minlen : Math.max(shape.height, minlen));
                const cell = lane.resizeCellSize
                if (!isNaN(shape.width) && !isNaN(cell.width) && cell.width > 0) shape.width = Math.ceil(shape.width / cell.width) * cell.width
                if (!isNaN(shape.height) && !isNaN(cell.height) && cell.height > 0) shape.height = Math.ceil(shape.height / cell.height) * cell.height
            }
        })
        // now do all of the usual stuff, according to whatever properties have been set on this GridLayout
        super.doLayout(coll)
        diagram.commitTransaction('PoolLayout')
    }

    // compute the minimum size for a particular Lane Group
    computeLaneSize(lane:any) {
        // assert(lane instanceof go.Group);
        const sz = new go.Size(lane.isSubGraphExpanded ? this.MINBREADTH : 1, this.MINLENGTH)
        if (lane.isSubGraphExpanded) {
            const holder = lane.placeholder
            if (holder !== null) {
                const hsz = holder.actualBounds
                sz.width = Math.max(sz.width, hsz.width)
            }
        }
        // minimum breadth needs to be big enough to hold the header
        const hdr = lane.findObject('HEADER')
        if (hdr !== null) sz.width = Math.max(sz.width, hdr.actualBounds.width)
        return sz
    }
}
  
const TechnicalShelf: FC = () => {    
    const dispatch = useDispatch()
   
    const route = useSelector((state: IAppState) => state.router)
    const { t } = useTranslation()
    const history: any = useHistory()
    const [goObj, setGoObj] = useState<any>(null)
    const [winW, setWinW] = useState(0)
    const [winH, setWinH] = useState(0)
    useEffect(() =>{
        const offsetW = 40
        const offsetH = 300
        setWinW(document.body.clientWidth - offsetW)
        setWinH(document.body.clientHeight - offsetH)
        const onResize = () => {
            setWinW(document.body.clientWidth - offsetW)
            setWinH(document.body.clientHeight - offsetH)
        }
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    },[])
    function initDiagram() {

        // Since 2.2 you can also author concise templates with method chaining instead of GraphObject.make
        // For details, see https://gojs.net/latest/intro/buildingObjects.html
        const $ = go.GraphObject.make
  
        const myDiagram =
          $(go.Diagram,
              {
                  // make sure the top-left corner of the viewport is occupied
                  contentAlignment: go.Spot.TopLeft,
                  // use a simple layout to stack the top-level Groups next to each other
                  layout: $(PoolLayout),
                  // disallow nodes to be dragged to the diagram's background
                  mouseDrop: (e:any) => {
                      e.diagram.currentTool.doCancel()
                  },
                  // a clipboard copied node is pasted into the original node's group (i.e. lane).
                  'commandHandler.copiesGroupKey': true,
                  // automatically re-layout the swim lanes after dragging the selection
                  'SelectionMoved': relayoutDiagram,  // this DiagramEvent listener is
                  'SelectionCopied': relayoutDiagram, // defined above
                  'undoManager.isEnabled': true,
                  // allow TextEditingTool to start without selecting first
                  'textEditingTool.starting': go.TextEditingTool.SingleClick,
              })
  
  
        // this is called after nodes have been moved
        function relayoutDiagram() {
            myDiagram.selection.each(n => n.invalidateLayout())
            myDiagram.layoutDiagram()
        }
  
        // There are only three note colors by default, blue, red, and yellow but you could add more here:
        const noteColors = ['#009CCC', '#CC293D', '#FFD700']
        function getNoteColor(num:any) {
            return noteColors[Math.min(num, noteColors.length - 1)]
        }
  
        myDiagram.nodeTemplate =
          $(go.Node, 'Horizontal',
              new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
              $(go.Shape, 'Rectangle', {
                  fill: '#009CCC', strokeWidth: 1, stroke: '#009CCC',
                  width: 6, stretch: go.GraphObject.Vertical, alignment: go.Spot.Left,
              },
              new go.Binding('fill', 'color', getNoteColor),
              new go.Binding('stroke', 'color', getNoteColor)
              ),
              $(go.Panel, 'Auto',
                  $(go.Shape, 'Rectangle', { fill: 'white', stroke: '#CCCCCC' }),
                  $(go.Panel, 'Table',
                      { width: 130, minSize: new go.Size(NaN, 50) },
                      $(go.TextBlock,
                          {
                              name: 'TEXT',
                              margin: 6, font: '11px Lato, sans-serif', editable: false,
                              stroke: '#000', maxSize: new go.Size(130, NaN),
                              alignment: go.Spot.TopLeft
                          },
                          new go.Binding('text', 'text').makeTwoWay())
                  )
              )
          )
  
        // While dragging, highlight the dragged-over group
  
        myDiagram.groupTemplate =
          $(go.Group, 'Vertical',
              {
                  selectable: false,
                  selectionObjectName: 'SHAPE', // even though its not selectable, this is used in the layout
                  layerName: 'Background',  // all lanes are always behind all nodes and links
                  layout: $(go.GridLayout,  // automatically lay out the lane's subgraph
                      {
                          wrappingColumn: 1,
                          cellSize: new go.Size(1, 1),
                          spacing: new go.Size(5, 5),
                          alignment: go.GridLayout.Position,
                          comparer: (a:any, b:any) => {  // can re-order tasks within a lane
                              const ay = a.location.y
                              const by = b.location.y
                              if (isNaN(ay) || isNaN(by)) return 0
                              if (ay < by) return -1
                              if (ay > by) return 1
                              return 0
                          }
                      }),
                  //   click: (e) => {  // allow simple click on group to clear selection
                  //       if (!e.shift && !e.control && !e.meta) e.diagram.clearSelection()
                  //   },
                  computesBoundsAfterDrag: true,  // needed to prevent recomputing Group.placeholder bounds too soon
                  handlesDragDropForMembers: true,  // don't need to define handlers on member Nodes and Links
                 
                  subGraphExpandedChanged: (grp:any) => {
                      const shp = grp.selectionObject
                      if (grp.diagram.undoManager.isUndoingRedoing) return
                      if (grp.isSubGraphExpanded) {
                          shp.width = grp.data.savedBreadth
                      } else {  // remember the original width
                          if (!isNaN(shp.width)) grp.diagram.model.set(grp.data, 'savedBreadth', shp.width)
                          shp.width = NaN
                      }
                  }
              },
              //   new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
              new go.Binding('isSubGraphExpanded', 'expanded').makeTwoWay(),
              // the lane header consisting of a TextBlock and an expander button
              $(go.Panel, 'Horizontal',
                  { name: 'HEADER', alignment: go.Spot.Left },
                  $('SubGraphExpanderButton', { margin: 5 }),  // this remains always visible
                  $(go.TextBlock,  // the lane label
                      { font: '15px Lato, sans-serif', editable: false, margin: new go.Margin(2, 0, 0, 0) },
                      // this is hidden when the swimlane is collapsed
                      new go.Binding('visible', 'isSubGraphExpanded').ofObject(),
                      new go.Binding('text').makeTwoWay())
              ),  // end Horizontal Panel
              $(go.Panel, 'Auto',  // the lane consisting of a background Shape and a Placeholder representing the subgraph
                  $(go.Shape, 'Rectangle',  // this is the resized object
                      { name: 'SHAPE', fill: '#F1F1F1', stroke: null, strokeWidth: 4 },  // strokeWidth controls space between lanes
                      new go.Binding('fill', 'isHighlighted', h => h ? '#D6D6D6' : '#F1F1F1').ofObject(),
                      new go.Binding('desiredSize', 'size', go.Size.parse).makeTwoWay(go.Size.stringify)),
                  $(go.Placeholder,
                      { padding: 12, alignment: go.Spot.TopLeft }),
                  $(go.TextBlock,  // this TextBlock is only seen when the swimlane is collapsed
                      {
                          name: 'LABEL', font: '15px Lato, sans-serif', editable: true,
                          angle: 90, alignment: go.Spot.TopLeft, margin: new go.Margin(4, 0, 0, 2)
                      },
                      new go.Binding('visible', 'isSubGraphExpanded', e => !e).ofObject(),
                      new go.Binding('text').makeTwoWay())
              )  // end Auto Panel
          )  // end Group
  
        // Set up an unmodeled Part as a legend, and place it directly on the diagram.
        myDiagram.add(
            $(go.Part, 'Table',
                { position: new go.Point(10, 10), selectable: false },
                $(go.TextBlock, `${t('WorkGroup')}`,
                    { row: 0, font: '700 14px Droid Serif, sans-serif',margin : new go.Margin(0,0,0,150)  }),  // end row 0
            )
        )
        setGoObj(myDiagram)
        return myDiagram
    } 
   
    const zoomToFit = () => {
        goObj && goObj.commandHandler.zoomToFit()
    }
   
    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/technicalShelf',
            breadcrumbName: t('technicalShelf'),
        },
    ]
    const [projectList, setProjectList] = useState<IProjectInfoResData[]>([])
    const viewLogList = async () => {
        const projectList =  await getAllProjectInfo() as IProjectInfoResData[]
        setProjectList(projectList)
    }
    const viewRoadList = async () => {
        const projectList =  await getAllTechnicalRoad() as IProjectInfoResData[]
        setProjectList(projectList)
    }
    
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)

    const [nodeDataArray, setNodeDataArray] = useState<any[]>([])
   
    useEffect(() => {
        const nodeDataArray: any[] = []
        workgroupList.forEach((item) =>{
            nodeDataArray.push(
                {
                    'key': item.groupName,
                    'text': item.groupName,
                    'isGroup': true,
                }
            )
        })
        projectList.forEach( (item :IProjectInfoResData)=>{
            let techName = ''
            if (item.techName.includes('"str":') && item.techName.includes('"obj":')) {
                techName = JSON.parse(item.techName).str
                techName = techName.replace(/<\/?.+?\/?>/g,'')
            } else {
                techName = item.techName
            }
            techName = techName.replace(/\n/g, '')
            if(item.subsidiary){
                techName += '\n' + item.subsidiary
            }
            nodeDataArray.push(
                {
                    'key': item.id,
                    'text': techName + '\n' + item.techPtn,
                    'group': item.techClass.workGroup.workGroupName,
                }
            )
        })
        // console.group(nodeDataArray)
        setNodeDataArray(nodeDataArray)
    }, [projectList])

    const [value, setValue] = useState(1)
    useEffect(() => {
        if(value == 2){
            viewRoadList()
        }else{
            viewLogList()
        }
    }, [value])

    const onChange = (e: RadioChangeEvent) => {
        setValue(e.target.value)
    }

    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <div  style={{ width: winW,height:winH }}>
                <Space direction='vertical' style={{ position: 'relative', width: '100%' }}>
                    <Radio.Group onChange={onChange} value={value}>
                        <Radio value={1}>{t('technicalShelf')}</Radio>
                        <Radio value={2}>{t('technicalRoad')}</Radio>
                    </Radio.Group>
                    <ReactDiagram
                        initDiagram={initDiagram}
                        divClassName='technical-shelf'
                        nodeDataArray={nodeDataArray}
                    />
                    <Space style={{ display: 'flex', justifyContent: 'flex-end',paddingRight:'50px' }}>
                        <Button onClick={zoomToFit}>{t('Zoom To Fit')}</Button>
                    </Space>
                </Space>
            </div>
            
        </Space>
    )
}

export default TechnicalShelf